export enum PriceSummaryJobStatus {
  PENDING = 'PENDING',
  INITIALISING = 'INITIALISING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  RETRYING = 'RETRYING',
  CANCELLED = 'CANCELLED',
}

export type PriceSummaryJob = {
  jobId: string;
  status: PriceSummaryJobStatus;
  createdAt: string;
  updatedAt: string;
  locationUrl: string;
  startDate: string;
  endDate: string;
  numberOfNights: number;
  currencyCode: string;
  countryCode: string;
  progress: number;
  days: number;
  submittedBy?: string;
  submittedByEmail?: string;
  cancelledBy?: string;
  cancelledByEmail?: string;
  retries: number;
};

export type PriceSummaryJobBody = {
  location_url: string;
  start_date: string;
  end_date: string;
  number_of_nights: number;
  currency_code: string;
  country_code: string;
};

export type PaginatedPriceParrotQueryParams = {
  page?: number;
  limit?: number;
  query?: string;
  status?: ScheduledPriceSummaryJobStatus;
  tag?: string;
};
export type PriceSummaryJobQueryParams = PaginatedPriceParrotQueryParams & {
  user_id?: string;
};

export type PaginatedPriceParrotResponse<T> = {
  data: Array<T>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

export enum ScheduleInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  ADHOC = 'ADHOC',
}

export enum ScheduledPriceSummaryJobStatus {
  PAUSED = 'PAUSED',
  ACTIVE = 'ACTIVE',
}

export type Property = {
  id: number;
  name?: string;
  location?: string;
  url: string;
};

export type ScheduledPriceSummaryJobVariation = {
  id: number;
  countryCode: string;
  currencyCode: string;
  numberOfNights: number;
  offsetDays: number;
  totalDays: number;
  createdAt: string;
};
export type ScheduledPriceSummaryJobVariationBody = Omit<ScheduledPriceSummaryJobVariation, 'id' | 'createdAt'>;

export type ScheduledPriceSummaryJob = {
  id: number;
  property: Property;
  status: ScheduledPriceSummaryJobStatus;
  scheduleInterval: ScheduleInterval;
  nextRunAt: string;
  createdBy?: string;
  createdByEmail?: string;
  updatedBy?: string;
  updatedByEmail?: string;
  createdAt: string;
  updatedAt: string;
  variations: Array<ScheduledPriceSummaryJobVariation>;
};

export type ScheduledPriceSummaryJobBody = {
  scheduleInterval: ScheduleInterval;
  locationUrl: string;
  scheduledJobVariations: Array<ScheduledPriceSummaryJobVariationBody>;
};

export type ScheduledPriceSummaryJobRun = {
  jobId: number;
  variation: ScheduledPriceSummaryJobVariation;
  status: PriceSummaryJobStatus;
  createdAt: string;
  updatedAt: string;
  retries: number;
  progress: number;
  days: number;
  errorMessage?: string;
};

// Control Centre types

/** Raw snake_case response from GET /api/vendor/parity/control-centre */
export type RawControlCentreProperty = {
  property_id: string | null;
  property_name: string | null;
  offer_bk: string;
  vendor_bk: string | null;
  vendor_name: string | null;
  impressions_overpriced_l30d: number;
  pct_impressions_overpriced_l30d: number | null;
  pct_overpriced_l30d: number | null;
  mpv_l7d: number | null;
  mpv_l30d: number | null;
  offer_country_code: string | null;
  offer_country_name: string | null;
  deal_tier: string | null;
  bedbank_lpc_ratio: number | null;
  yoy_ttv_change: number | null;
  vns_days: number | null;
  sf_opportunity_status: string | null;
  sf_last_activity: string | null;
  sf_closed_date: string | null;
  sf_created_date: string | null;
};

export type RawControlCentreResponse = {
  data: Array<RawControlCentreProperty>;
  total: number;
};

/** Frontend camelCase property used by the DataGrid */
export type ControlCentreProperty = {
  propertyId: string;
  propertyName: string;
  offerBk: string;
  vendorBk: string | null;
  offerCountryCode: string | null;
  offerCountryName: string | null;
  impressionsOverpriced: number;
  pctImpressionsOverpriced: number;
  pctOverpriced: number;
  mpv7d: number;
  mpv30d: number;
  connectivityActionStatus: string | null;
  lastActivityDate: string | null;
  closedDate: string | null;
  createdDate: string | null;
  vnsDays7d: number | null;
  openOpp: boolean;
  bedbankLpcPct: number | null;
  yoyDeclinePct: number | null;
  tier: number | null;
};

export type ControlCentreSummary = {
  propertiesFlagged: number;
  openOpportunities: number;
  casesCreatedToday: number;
  avgOverpricedImpressionsPct: number;
};

// Mutation types

export type CreateSfOpportunityBody = {
  propertyId: string;
};

export type CreateSfOpportunityResponse = {
  success: boolean;
  opportunity_id: string | null;
};

export type TriggerNotificationBody = {
  propertyId: string;
};

export type TriggerNotificationResponse = {
  success: boolean;
  opportunity_id: string | null;
  notification_triggered: boolean;
};

export type BulkCreateSfOpportunitiesBody = {
  items: Array<{ offer_bk: string; vendor_bk: string }>;
  reason?: string;
};

export type BulkCreateSfOpportunitiesResponse = {
  created: number;
  failed: number;
  errors?: Array<{ offer_bk: string; message: string }>;
};

/** Range-scoped summary fields from GET /api/vendor/parity/trend/offers/:offer_bk/summary */
export type ParityTrendSummary = {
  overpriced_searches: number;
  total_searches: number;
  /** Fraction e.g. 0.12 = 12% */
  avg_price_diff_when_overpriced: number | null;
};

export type ParityTrendDailyPoint = {
  report_date: string;
  region: string;
  /** Fraction 0–1 */
  pct_overpriced: number | null;
  overpriced_searches: number;
  total_searches: number;
};

export type ParityTrendCurrencyPoint = {
  currency: string;
  avg_price_diff_to_main: number | null;
};

/** GET /api/vendor/parity/trend/offers/:offer_bk/series */
export type ParityTrendSeriesResult = {
  daily_trend: Array<ParityTrendDailyPoint>;
};

export type ParityTrendSeriesApiResponse = {
  data: ParityTrendSeriesResult;
};

/** GET /api/vendor/parity/trend/offers/:offer_bk/summary */
export type ParityTrendRangeSummaryResult = {
  summary: ParityTrendSummary;
  by_currency: Array<ParityTrendCurrencyPoint>;
};

export type ParityTrendRangeSummaryApiResponse = {
  data: ParityTrendRangeSummaryResult;
};
